import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ShowChartIcon from "@material-ui/icons/ShowChart";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  card: {
    height: "100%",
    position: "relative",
  },
  cardIcon: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    opacity: 0.2,
    fontSize: "3rem",
  },
  cardTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  cardSubtext: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  statusCard: {
    padding: theme.spacing(3),
    textAlign: "center",
    minHeight: 200,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  statusIcon: {
    fontSize: "4rem",
    marginBottom: theme.spacing(2),
  },
  statusTitle: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  statusText: {
    fontSize: "1rem",
    marginBottom: theme.spacing(2),
  },
  recommendationCard: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    minHeight: 200,
  },
  recommendationTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  recommendationValue: {
    fontSize: "3rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  recommendationLabel: {
    fontSize: "1rem",
    opacity: 0.9,
  },
  progressContainer: {
    marginTop: theme.spacing(2),
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: "center",
    padding: theme.spacing(3),
  },
});

const hebrewLabels = {
  title: "תחזיות מכירות ומלאי",
  ytdSales: "מכירות מתחילת השנה",
  projectedEOY: "תחזית סוף שנה",
  remainingDemand: "ביקוש צפוי נותר",
  stockStatus: "מצב מלאי",
  currentStock: "מלאי נוכחי",
  purchaseRecommendation: "המלצת רכש",
  daysOfStock: "ימי מלאי",
  units: "יחידות",
  orders: "הזמנות",
  revenue: "הכנסות",
  status: {
    sufficient: "מלאי מספיק",
    deficit: "חוסר צפוי",
    surplus: "עודף מלאי",
  },
  statusDetails: {
    sufficient: "המלאי הנוכחי צפוי להספיק עד סוף השנה",
    deficit: "נדרש לרכוש מלאי נוסף כדי לעמוד בביקוש הצפוי",
    surplus: "המלאי הנוכחי עולה על הביקוש הצפוי",
  },
  purchaseText: "יחידות לרכישה",
  daysRemaining: "ימי מלאי נותרים",
  loading: "טוען נתונים...",
  error: "שגיאה בטעינת הנתונים",
  noData: "אין נתונים זמינים",
  progressToTarget: "התקדמות לעומת תחזית",
};

class ProductProjections extends React.Component {
  _isMounted = false;

  state = {
    loading: true,
    error: null,
    projections: null,
  };

  componentDidMount() {
    this._isMounted = true;
    this.loadProjections();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.product?.ean !== this.props.product?.ean) {
      this.loadProjections();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadProjections = async () => {
    const { product } = this.props;
    if (!product?.ean) return;

    this.setState({ loading: true, error: null });

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/product-analytics/${product.ean}/projections`,
        { headers: utils.getAdminAuthHeaders() }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch projections");
      }

      const data = await response.json();

      if (this._isMounted) {
        // API returns { ok: true, projections: {...} }
        // Extract the nested projections object
        const projectionsData = data.ok && data.projections 
          ? data.projections 
          : data;
        
        console.log('[ProductProjections] Loaded data:', {
          hasYtd: !!projectionsData.ytd,
          hasProjections: !!projectionsData.projections,
          hasStockAnalysis: !!projectionsData.stockAnalysis,
          ytdUnits: projectionsData.ytd?.totalUnits,
        });
        
        this.setState({
          loading: false,
          projections: projectionsData,
        });
      }
    } catch (error) {
      console.error("Error loading projections:", error);
      if (this._isMounted) {
        this.setState({
          loading: false,
          error: error.message,
        });
      }
    }
  };

  formatNumber = (num) => {
    return num?.toLocaleString() || "0";
  };

  formatCurrency = (num) => {
    return `₪${num?.toLocaleString() || 0}`;
  };

  getStockStatusConfig = (status) => {
    switch (status) {
      case "sufficient":
        return {
          icon: <CheckCircleIcon />,
          color: "#4caf50",
          label: hebrewLabels.status.sufficient,
          details: hebrewLabels.statusDetails.sufficient,
        };
      case "deficit":
        return {
          icon: <WarningIcon />,
          color: "#ff9800",
          label: hebrewLabels.status.deficit,
          details: hebrewLabels.statusDetails.deficit,
        };
      case "surplus":
        return {
          icon: <CheckCircleIcon />,
          color: "#2196f3",
          label: hebrewLabels.status.surplus,
          details: hebrewLabels.statusDetails.surplus,
        };
      default:
        return {
          icon: <WarningIcon />,
          color: "#9e9e9e",
          label: "לא זמין",
          details: "",
        };
    }
  };

  renderYTDSection() {
    const { classes } = this.props;
    const { projections } = this.state;

    if (!projections?.ytd) return null;

    const ytd = projections.ytd || { totalUnits: 0, totalRevenue: 0, totalProfit: 0 };

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.card}>
            <CardContent>
              <ShowChartIcon className={classes.cardIcon} style={{ color: "#1976d2" }} />
              <Typography className={classes.cardTitle}>
                {hebrewLabels.units}
              </Typography>
              <Typography className={classes.cardValue} style={{ color: "#1976d2" }}>
                {this.formatNumber(ytd.totalUnits)}
              </Typography>
              <Typography className={classes.cardSubtext}>
                {hebrewLabels.ytdSales}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.card}>
            <CardContent>
              <ShoppingCartIcon
                className={classes.cardIcon}
                style={{ color: "#2e7d32" }}
              />
              <Typography className={classes.cardTitle}>
                {hebrewLabels.orders}
              </Typography>
              <Typography className={classes.cardValue} style={{ color: "#2e7d32" }}>
                {this.formatNumber(ytd.totalOrders)}
              </Typography>
              <Typography className={classes.cardSubtext}>
                {hebrewLabels.ytdSales}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.card}>
            <CardContent>
              <TrendingUpIcon className={classes.cardIcon} style={{ color: "#ed6c02" }} />
              <Typography className={classes.cardTitle}>
                {hebrewLabels.revenue}
              </Typography>
              <Typography className={classes.cardValue} style={{ color: "#ed6c02" }}>
                {this.formatCurrency(ytd.totalRevenue)}
              </Typography>
              <Typography className={classes.cardSubtext}>
                {hebrewLabels.ytdSales}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  renderProjectionsSection() {
    const { classes } = this.props;
    const { projections } = this.state;

    if (!projections?.projections || !projections?.ytd) return null;

    const proj = projections.projections;
    const ytd = projections.ytd;
    const progressPct = ytd.totalUnits > 0
      ? Math.min(100, (ytd.totalUnits / proj.projectedEndOfYearUnits) * 100)
      : 0;

    return (
      <Paper className={classes.paper}>
        <Box className={classes.sectionHeader}>
          <TrendingUpIcon color="primary" />
          <Typography variant="h6">{hebrewLabels.projectedEOY}</Typography>
        </Box>
        <Divider />

        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.cardTitle}>
                    {hebrewLabels.projectedEOY} - {hebrewLabels.units}
                  </Typography>
                  <Typography className={classes.cardValue} style={{ color: "#1976d2" }}>
                    {this.formatNumber(proj.projectedEndOfYearUnits)}
                  </Typography>
                  <Typography className={classes.cardSubtext}>
                    יחידות צפויות
                  </Typography>

                  <Box className={classes.progressContainer}>
                    <div className={classes.progressLabel}>
                      <Typography variant="caption">
                        {hebrewLabels.progressToTarget}
                      </Typography>
                      <Typography variant="caption">
                        {progressPct.toFixed(1)}%
                      </Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={progressPct}
                      className={classes.progressBar}
                      style={{
                        backgroundColor: "#e3f2fd",
                      }}
                      sx={{
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#1976d2",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.cardTitle}>
                    {hebrewLabels.remainingDemand}
                  </Typography>
                  <Typography className={classes.cardValue} style={{ color: "#ed6c02" }}>
                    {this.formatNumber(proj.remainingDemand)}
                  </Typography>
                  <Typography className={classes.cardSubtext}>
                    יחידות צפויות עד סוף השנה
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    );
  }

  renderStockAnalysis() {
    const { classes, product } = this.props;
    const { projections } = this.state;

    if (!projections?.stockAnalysis) return null;

    const stock = projections.stockAnalysis;
    const statusConfig = this.getStockStatusConfig(stock.status);

    return (
      <Paper className={classes.paper}>
        <Box className={classes.sectionHeader}>
          <WarningIcon color="primary" />
          <Typography variant="h6">{hebrewLabels.stockStatus}</Typography>
        </Box>
        <Divider />

        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                className={classes.statusCard}
                style={{ backgroundColor: `${statusConfig.color}10` }}
              >
                <div
                  className={classes.statusIcon}
                  style={{ color: statusConfig.color }}
                >
                  {statusConfig.icon}
                </div>
                <Chip
                  label={statusConfig.label}
                  style={{
                    backgroundColor: statusConfig.color,
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                />
                <Typography className={classes.statusText} style={{ marginTop: 16 }}>
                  {statusConfig.details}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {hebrewLabels.currentStock}: {this.formatNumber(product.stock)}{" "}
                  {hebrewLabels.units}
                </Typography>
                {stock.daysOfStockRemaining && (
                  <Typography variant="body2" color="textSecondary">
                    {hebrewLabels.daysRemaining}: ~{stock.daysOfStockRemaining} ימים
                  </Typography>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {stock.recommendedPurchaseQuantity > 0 && (
                <Card className={classes.recommendationCard}>
                  <CardContent>
                    <Typography className={classes.recommendationTitle}>
                      {hebrewLabels.purchaseRecommendation}
                    </Typography>
                    <Typography className={classes.recommendationValue}>
                      {this.formatNumber(stock.recommendedPurchaseQuantity)}
                    </Typography>
                    <Typography className={classes.recommendationLabel}>
                      {hebrewLabels.purchaseText}
                    </Typography>
                    <Box mt={3}>
                      <Typography variant="body2" style={{ opacity: 0.9 }}>
                        המלצה מבוססת על ביקוש צפוי עד סוף השנה ומלאי נוכחי
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
              {stock.recommendedPurchaseQuantity === 0 && (
                <Card
                  className={classes.statusCard}
                  style={{ backgroundColor: "#e8f5e9" }}
                >
                  <CheckCircleIcon
                    className={classes.statusIcon}
                    style={{ color: "#4caf50" }}
                  />
                  <Typography className={classes.statusTitle}>
                    אין צורך ברכש נוסף
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    המלאי הנוכחי צפוי להספיק עד סוף השנה
                  </Typography>
                </Card>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
    );
  }

  render() {
    const { classes, product } = this.props;
    const { loading, error, projections } = this.state;

    if (!product) return null;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <Typography className={classes.errorText}>
          {hebrewLabels.error}: {error}
        </Typography>
      );
    }

    if (!projections) {
      return (
        <Typography variant="body1" color="textSecondary" align="center">
          {hebrewLabels.noData}
        </Typography>
      );
    }

    return (
      <div className={classes.root}>
        {/* YTD Sales */}
        <Paper className={classes.paper}>
          <Box className={classes.sectionHeader}>
            <ShowChartIcon color="primary" />
            <Typography variant="h6">{hebrewLabels.ytdSales}</Typography>
          </Box>
          <Divider />
          <Box mt={3}>{this.renderYTDSection()}</Box>
        </Paper>

        {/* Projections */}
        {this.renderProjectionsSection()}

        {/* Stock Analysis */}
        {this.renderStockAnalysis()}
      </div>
    );
  }
}

ProductProjections.propTypes = {
  classes: PropTypes.object.isRequired,
  product: PropTypes.object,
};

export default withStyles(styles)(ProductProjections);
